import React, { useEffect, useState } from "react";
import { crm } from "@/lib/crm";
import { formatPrice } from "@/lib/format";
import { Loader2, Lightbulb, ArrowRight, Target, BarChart3, Plane, ExternalLink, ShieldCheck } from "lucide-react";

const SPEED_TONE = {
  fast: { text: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  normal: { text: "text-sky-300", bg: "bg-sky-500/10 border-sky-500/30" },
  slow: { text: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
  vslow: { text: "text-rose-300", bg: "bg-rose-500/10 border-rose-500/30" },
};

const posColor = (p) => (p < 40 ? "#16A34A" : p <= 70 ? "#F59E0B" : "#E11D48");

export default function PriceAdvice({ carId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cross, setCross] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    crm(`/market/price-advice/${carId}`)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setData({ has_data: false }); setLoading(false); } });
    return () => { alive = false; };
  }, [carId]);

  useEffect(() => {
    let alive = true;
    crm(`/market/pro/cross-border/${carId}`)
      .then((d) => { if (alive) setCross(d); })
      .catch(() => { if (alive) setCross({ has_data: false }); });
    return () => { alive = false; };
  }, [carId]);

  if (loading) {
    return (
      <div data-testid="price-advice-loading" className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
        <Loader2 size={16} className="animate-spin text-pactly-blue" /> Analyse du marché en cours…
      </div>
    );
  }

  if (!data?.has_data) {
    return (
      <div data-testid="price-advice-nodata" className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4 text-center text-sm text-white/50">
        📊 Pas de données marché disponibles pour ce véhicule
      </div>
    );
  }

  const m = data.market || {};
  const speed = data.sale_speed || {};
  const st = SPEED_TONE[speed.key] || SPEED_TONE.normal;
  const posP = data.positioning_pct ?? 50;
  const delta = data.delta_pct;

  return (
    <div data-testid="price-advice" className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center gap-2 text-sm font-extrabold text-white">
        <Lightbulb size={16} className="text-amber-300" /> Conseil Prix
      </div>

      {/* Your price vs market median */}
      <div className="mt-3 flex items-center justify-between rounded-xl border border-white/8 bg-white/5 p-3">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Votre prix</div>
          <div className="font-display text-lg font-extrabold text-white" data-testid="advice-your-price">{formatPrice(data.car_price)}</div>
        </div>
        <div className="flex flex-col items-center px-2 text-center">
          <ArrowRight size={16} className="text-white/40" />
          <span className={`text-xs font-bold ${delta > 5 ? "text-rose-400" : delta < -5 ? "text-emerald-400" : "text-amber-400"}`}>
            {delta > 0 ? "+" : ""}{delta}%
          </span>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wide text-white/40">Médiane marché</div>
          <div className="font-display text-lg font-extrabold text-white" data-testid="advice-market-median">{formatPrice(m.median)}</div>
        </div>
      </div>

      {/* Position bar */}
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[11px] text-white/50">
          <span className="flex items-center gap-1"><BarChart3 size={12} /> Positionnement marché</span>
          <span className="font-bold" style={{ color: posColor(posP) }}>{posP < 40 ? "Compétitif" : posP <= 70 ? "Dans la moyenne" : "Élevé"}</span>
        </div>
        <div className="relative h-2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full transition-all" style={{ width: `${posP}%`, background: posColor(posP) }} data-testid="advice-position-bar" />
        </div>
      </div>

      {/* Suggestion box */}
      <div className="mt-3 rounded-xl border border-sky-500/30 bg-sky-500/10 p-3" data-testid="advice-suggestion">
        <div className="flex items-center gap-1.5 text-xs font-bold text-sky-200">
          <Target size={13} /> Suggestion pour vente rapide (&lt; 30 jours)
        </div>
        <div className="mt-1.5 text-sm text-white/85">
          Fourchette conseillée : <b className="text-white">{formatPrice(data.suggested?.low)} – {formatPrice(data.suggested?.high)}</b>
        </div>
        <div className="text-sm text-white/85">Prix optimal : <b className="text-sky-200">{formatPrice(data.suggested?.optimal)}</b></div>
      </div>

      {/* Sale speed */}
      <div className={`mt-3 flex items-center gap-3 rounded-xl border p-3 ${st.bg}`} data-testid="advice-sale-speed">
        <span className="text-2xl" aria-hidden>{speed.icon}</span>
        <div>
          <div className={`text-sm font-extrabold ${st.text}`}>Estimation de vente : {speed.label} · ~{speed.days} jours</div>
          <div className="text-[11px] text-white/50">Basé sur {data.total_sample_size} annonces similaires</div>
        </div>
      </div>

      {/* Tips */}
      {Array.isArray(data.tips) && data.tips.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="advice-tips">
          {data.tips.map((tp, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-white/65">
              <span className="mt-0.5 text-amber-300">•</span> {tp}
            </li>
          ))}
        </ul>
      )}

      {/* Sourcing Terminal — cross-border import intelligence (PRO exclusive) */}
      {cross?.has_data && cross.best_saving && (
        <div data-testid="sourcing-cross-border"
          className={`mt-4 rounded-xl border p-3 ${cross.best_saving.is_gold ? "sourcing-gold border-[#C9D2DC]/40" : "border-white/10 bg-white/[0.03]"}`}>
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-extrabold text-white">
              <Plane size={13} className="text-pactly-blue" /> Sourcing Europe
            </span>
            <span data-testid="sourcing-elite-badge" className="inline-flex items-center gap-1 rounded-md border border-[#C9D2DC]/40 bg-[#C9D2DC]/10 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#C9D2DC]">
              <ShieldCheck size={10} /> Exclusivité Sourcing Élite
            </span>
          </div>
          <div className="space-y-1">
            {cross.countries.map((c) => (
              c.source?.url ? (
                <a key={c.code} href={c.source.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`sourcing-country-${c.code}`}
                  className="flex items-center justify-between rounded-lg px-2 py-1.5 text-xs transition hover:bg-white/5">
                  <span className="flex items-center gap-1.5 text-white/75">
                    <span aria-hidden>{c.flag}</span> {c.label}
                    <ExternalLink size={11} className="text-white/40" />
                  </span>
                  <span className="font-bold text-white">{formatPrice(c.median)}</span>
                </a>
              ) : (
                <div key={c.code} data-testid={`sourcing-country-${c.code}`} className="flex items-center justify-between px-2 py-1.5 text-xs">
                  <span className="flex items-center gap-1.5 text-white/75"><span aria-hidden>{c.flag}</span> {c.label}</span>
                  <span className="font-bold text-white">{formatPrice(c.median)}</span>
                </div>
              )
            ))}
          </div>
          <div data-testid="sourcing-profit" className={`mt-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${cross.best_saving.is_gold ? "bg-[#C9D2DC]/10 text-[#E8EEF5]" : "bg-emerald-500/10 text-emerald-300"}`}>
            {cross.best_saving.is_gold ? "🥇" : <Plane size={14} className="mt-0.5 shrink-0" />}
            <span>
              Potentiel de profit : <b>+{formatPrice(cross.best_saving.amount)}</b> via {cross.best_saving.flag} {cross.best_saving.label} (−{cross.best_saving.pct}%)
              {cross.best_saving.source?.url && (
                <> · <a href={cross.best_saving.source.url} target="_blank" rel="noopener noreferrer" className="underline">voir la source</a></>
              )}
            </span>
          </div>
          {cross.best_saving.landed_price != null && false && (
            <div data-testid="sourcing-approach-cost" className="mt-1.5" />
          )}
        </div>
      )}

      {/* Sources footer (anonymized) */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/8 pt-2 text-[11px] text-white/45">
        {(data.sources || []).map((s, i) => (
          <span key={i} className="inline-flex items-center gap-1" data-testid={`advice-source-${i}`}>
            <span aria-hidden>{s.flag}</span> {s.label} ({s.sample_size} annonces)
          </span>
        ))}
        {data.reliability !== "high" && <span className="font-semibold text-amber-400">⚠️ Données limitées</span>}
      </div>
    </div>
  );
}
